/**
 * Estate filters (task B4, amended by §A PR-5).
 *
 * **Status options are the union of the curated vocabulary and the rows the
 * API actually returned.** §13.8 records that the upstream never documents
 * the wire spelling of a SIM status, so observed spellings must always
 * appear — a hard-coded list alone silently stops matching the day the
 * upstream changes a string. But observed-only was the activation trap: the
 * filter offered only statuses on the *current page*, so "show me AVAILABLE
 * SIMs" was impossible unless one was already visible. The union keeps both
 * guarantees: the known statuses are always offerable, and an unrecognised
 * one still shows up the moment the wire sends it.
 *
 * The brand filter is hidden under `SUPPRESS`: a filter that can only ever
 * return nothing is a trap, and offering it would also disclose that a brand
 * dimension exists (§05.7).
 */

import type { BasketSummary, SimSummary } from '@rd/shared';
import { useMemo, type ReactElement, type ReactNode } from 'react';

import { Button } from '../../components/Button.js';
import { Field } from '../../components/Field.js';
import { Input, Select } from '../../components/Input.js';
import { normaliseStatus } from '../sims/actions/actionRegistry.js';
import { KNOWN_SIM_STATUSES } from '../sims/statusTone.js';

import type { SimFilterState } from './useInventory.js';

/**
 * The statuses this basket actually contains, and nothing else.
 *
 * It used to append the whole canonical vocabulary, so an estate holding only
 * ACTIVE and INITIAL SIMs offered ten choices, eight of which return an empty
 * table. The basket's own counts are authoritative — the network reports them
 * per basket — so a status with a zero count is not offered at all.
 *
 * Statuses observed on the page are still unioned in: a spelling the counts do
 * not model (§13.8 — new spellings are information) must remain filterable
 * rather than disappear.
 */
export function statusOptions(sims: SimSummary[], counts?: BasketCounts | null): string[] {
  const observed = [...new Set(sims.map((s) => s.status).filter((s): s is string => Boolean(s)))];
  const seen = new Set(observed.map((s) => normaliseStatus(s)));

  const fromCounts = counts
    ? COUNTED_STATUSES.filter(
        ([status, key]) => (counts[key] ?? 0) > 0 || ALWAYS_OFFERED.has(status),
      )
        .map(([status]) => status)
        .filter((status) => !seen.has(normaliseStatus(status)))
    : KNOWN_SIM_STATUSES.filter((status) => !seen.has(normaliseStatus(status)));

  return [...observed, ...fromCounts].sort();
}

/**
 * Statuses offered even at a count of zero.
 *
 * IN_PROGRESS is transient: a SIM enters it the moment an activation or swap is
 * submitted, so the count read a minute ago says nothing about whether the
 * operator wants to filter for it now — which is exactly when they do, to watch
 * what they just started.
 */
const ALWAYS_OFFERED = new Set(['IN_PROGRESS']);

/** Each canonical status beside the basket count that proves it is present. */
const COUNTED_STATUSES: readonly (readonly [string, keyof BasketCounts])[] = [
  ['ACTIVE', 'active'],
  ['AVAILABLE', 'available'],
  ['TEST_MODE', 'testMode'],
  ['SAFE_CUSTODY', 'safeCustody'],
  ['INACTIVE', 'inactive'],
  ['IN_PROGRESS', 'inProgress'],
  ['SUSPENDED', 'suspended'],
] as const;

type BasketCounts = NonNullable<BasketSummary['counts']>;

/**
 * One field of the bar.
 *
 * `basis-full` below `sm` and a content-sized basis above it is what makes the
 * group collapse honestly: on a phone each control takes the full width and
 * stacks, and on anything wider they share one line and flex into whatever is
 * left. A `grid-cols-4` — what this was — gives a 380px viewport four columns
 * of 80px, and a `<select>` at 80px shows about two characters of a status.
 *
 * `min-w-0` is load-bearing rather than defensive: a flex item's default
 * minimum is its content, so without it the longest status option sets the
 * column's floor and the bar overflows its own card instead of wrapping.
 */
function Cell({ basis, children }: { basis: string; children: ReactNode }): ReactElement {
  return <div className={`min-w-0 flex-1 basis-full ${basis}`}>{children}</div>;
}

export function SimFilters({
  sims,
  basket,
  value,
  onChange,
  leading,
}: {
  sims: SimSummary[];
  /** The selected basket, whose counts say which statuses it holds. */
  basket?: BasketSummary | null;
  value: SimFilterState;
  onChange(next: SimFilterState): void;
  /**
   * An optional control that belongs to this group but is not one of its
   * filters — the estate's basket picker.
   *
   * It is a slot rather than a `basket`/`onBasketChange` pair because the
   * basket is not part of `SimFilterState`: it is a path segment on a
   * different query, cleared by nothing this form's Clear button does, and
   * owned by the page. What it shares with the fields is only that an operator
   * reads it as one bar of controls — which is a layout fact, and layout is
   * what this component is for.
   */
  leading?: ReactNode;
}): ReactElement {
  const statuses = useMemo(() => statusOptions(sims, basket?.counts), [sims, basket]);

  return (
    /*
      One framed group rather than four fields floating on the canvas.
      Everything above the table that changes what the table shows is inside
      this one card, so "what am I looking at" is a single object with a single
      edge — and the table below it, on the same bright plane, reads as the
      answer to it rather than as an unrelated second panel.

      `--elevation-0`: the bar is a control surface, not a floating one. It sits
      on the page beside the table exactly as the table sits on the page.
    */
    <form
      className="card mb-[var(--space-4)] p-[var(--space-3)] shadow-[var(--elevation-0)]"
      onSubmit={(e) => e.preventDefault()}
      aria-label="Filter SIMs"
    >
      <div className="flex flex-wrap items-end gap-[var(--space-3)]">
        {leading ? <Cell basis="sm:basis-[11rem]">{leading}</Cell> : null}

        <Cell basis="sm:basis-[10rem]">
          <Field label="Status">
            {(props) => (
              <Select
                {...props}
                value={value.simStatus ?? ''}
                onChange={(e) => onChange({ ...value, simStatus: e.target.value || undefined })}
              >
                <option value="">Any status</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </Cell>

        <Cell basis="sm:basis-[10rem]">
          <Field label="Search by">
            {(props) => (
              <Select
                {...props}
                value={value.simFilterType ?? ''}
                onChange={(e) =>
                  onChange({
                    ...value,
                    simFilterType:
                      e.target.value === '' ? undefined : (e.target.value as 'MSISDN' | 'SIM_NO'),
                  })
                }
              >
                {/*
                  Neither field is the default, because an operator holding a
                  number in their hand often cannot say which of the two it is
                  — being made to choose first was the complaint. Sent with no
                  field, the API tries one and, if that matched nothing, the
                  other.

                  The two explicit choices stay for the one case automatic
                  cannot serve: a fragment matching rows in BOTH fields shows
                  the first field's rows only, and pinning is how an operator
                  asks for the other set.

                  `SIM_NO`, not `ICCID` or `SIM`: those return 400. The working
                  spelling is undocumented and was found by probing the gateway.
                */}
                <option value="">Mobile or SIM number</option>
                <option value="MSISDN">Mobile number</option>
                <option value="SIM_NO">SIM number</option>
              </Select>
            )}
          </Field>
        </Cell>

        <Cell basis="sm:basis-[14rem]">
          <Field label="Value">
            {(props) => (
              <Input
                {...props}
                value={value.filterValue ?? ''}
                inputMode="numeric"
                /*
                  The placeholder says which number this box wants, because the
                  label cannot: "Value" is the API's word for it and the
                  field's meaning changes with the control beside it. It also
                  states the match, which is the one thing an operator has to
                  know to use the box at all — ANY part of the number, not the
                  start of it: both fields match on a substring, so the last
                  digits read off a SIM find the row.
                */
                placeholder={
                  value.simFilterType === 'SIM_NO'
                    ? 'Any part of a SIM number'
                    : value.simFilterType === 'MSISDN'
                      ? 'Any part of a mobile number'
                      : 'Any part of a mobile or SIM number'
                }
                onChange={(e) => onChange({ ...value, filterValue: e.target.value || undefined })}
              />
            )}
          </Field>
        </Cell>

        {/*
          No Brand filter. It was built from `availablePlans`, which the API no
          longer sends this tenant, so it offered nothing to filter by — and on
          an estate served by a single operator it never separated two SIMs
          anyway.
        */}

        {/* Pushed to the far end of the bar on a wide viewport and full-width
            below `sm`, so it never sits half-width beside a wrapped field
            looking like a fourth control with a missing label. */}
        <div className="basis-full sm:ml-auto sm:basis-auto">
          <Button variant="secondary" className="w-full sm:w-auto" onClick={() => onChange({})}>
            Clear filters
          </Button>
        </div>
      </div>
    </form>
  );
}
